// Fill editor for ParamField type "fill" (FillValue contract in
// posterTools/types.ts). The old INLINE EXPANDER (swatch + Solid/Gradient/
// Texture tabs opening in-place) is RETIRED — owner, 2026-07-23, live-testing
// the Smart Tool modal: "every color selector needs to be our uniform color
// picker with color/gradient/texture". This now renders the house PaintField
// chip, which opens the ONE PaintPickerModal (Color/Gradient/Texture tabs,
// shared Esc stack, z 12000 — above the tool modal's 11000, so paint closes
// first).
//
// GRAMMAR BRIDGE — posterTools FillValue vs the house paint model.
//   Solid    bare-string '#hex' / { type:'solid', color }        — SAME shape
//   Gradient { type:'linear-gradient', stops[]|from/to, angle }  — SAME shape
//            { type:'radial-gradient', stops[] }                 — SAME shape
//   Texture  posterTools: { type:'texture', url,  scale? }
//            house paint:  { type:'texture', id | src, scale? }  — DIFFERS
// resolveFill (fill.ts) reads exactly what the house emits for solids and
// gradients, so those pass THROUGH untouched. ONLY texture needs translating:
//   · out of the picker → posterTools: id|src collapse to a loadable `url`
//     (textureSrcOf resolves a manifest id to its bundled url, or returns the
//     imported data-url src verbatim);
//   · into the picker ← posterTools: a stored { url } is fed as { src } so the
//     chip face + texture pane render it (paintCss/textureSrcOf read src|id).
//
// allow = solid+gradient+texture is HONEST for every `fill` param: they are all
// consumed through applyFill/resolveFill (verified 2026-07-23 — decor
// star/panel/badge/speech/ribbon, art-text front/face, data-table header,
// math dice face), which renders all three modes. Plain `color`-type params
// (raw hex strings, no resolveFill) get the solid-only PaintField instead — see
// SmartObjectFieldControl.
import React from 'react';
import { PaintField, textureSrcOf } from './primitives/PaintPicker';
import { fillModeLabel } from './SmartObjectFieldUtils';

/** House paint value (out of the picker) → posterTools FillValue. Only texture
 *  changes dialect; solids (string / {solid}) and gradients pass through. */
function paintToFill(next) {
  if (next && typeof next === 'object' && next.type === 'texture') {
    const url = textureSrcOf(next); // src ?? textureUrl(id)
    return url ? { type: 'texture', url, scale: next.scale ?? 1 } : next;
  }
  return next;
}

/** posterTools FillValue → house paint value. A stored { type:'texture', url }
 *  (no id/src) reads as { src } so the house chip + texture pane render it. */
function fillToPaint(value) {
  if (
    value && typeof value === 'object' && value.type === 'texture'
    && value.url && !value.src && !value.id
  ) {
    return { type: 'texture', src: value.url, scale: value.scale ?? 1 };
  }
  return value;
}

export default function SmartObjectFillField({ value, onChange, label, dataField }) {
  return (
    <div className="cce-so-fill">
      <PaintField
        label={label || 'Fill'}
        dataField={dataField}
        value={fillToPaint(value)}
        allow={['solid', 'gradient', 'texture']}
        chipClassName="cce-so-fill-chip"
        onChange={(next) => onChange(paintToFill(next))}
      />
      <span className="cce-so-fill-sum">{fillModeLabel(value)}</span>
    </div>
  );
}
